/* /app/incidenten — Incidentenregistratie voor GGD-inspectieproof logging. */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StaffPinModal from "@/components/StaffPinModal";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AlertTriangle, Plus, X, Filter, CheckCircle2, Clock, FileDown, Send, ThumbsUp, ThumbsDown, RotateCcw, Inbox, Loader2, Settings } from "lucide-react";

const ERNST_KLEUR = {
  laag: "bg-emerald-100 text-emerald-800 border-emerald-200",
  midden: "bg-amber-100 text-amber-800 border-amber-200",
  hoog: "bg-orange-100 text-orange-800 border-orange-200",
  kritiek: "bg-red-100 text-red-800 border-red-300",
};

const TYPE_LABEL = {
  "ongeval": "Ongeval", "bijna-ongeval": "Bijna-ongeval", "gedrag": "Gedrag",
  "medisch": "Medisch", "veiligheid": "Veiligheid", "brand-oefening": "Brandoefening",
  "verdachte-situatie": "Verdachte situatie", "overig": "Overig",
};

export default function IncidentenPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [types, setTypes] = useState([]);
  const [ernsten, setErnsten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filterErnst, setFilterErnst] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [tab, setTab] = useState("alle");  // 'alle' | 'inbox'
  const [inbox, setInbox] = useState([]);
  const [reviewingId, setReviewingId] = useState(null);  // incident-id om te reviewen
  const [submittingId, setSubmittingId] = useState(null);  // incident-id om te submitten
  const [me, setMe] = useState(null);
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/auth/me"); setMe(r.data); } catch {}
    })();
  }, []);

  const loadInbox = async () => {
    try {
      const r = await api.get("/incidenten-inbox");
      setInbox(r.data?.items || []);
    } catch { setInbox([]); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterErnst !== "all") params.set("ernst", filterErnst);
      if (filterType !== "all") params.set("type", filterType);
      const r = await api.get(`/incidenten?${params}`);
      setItems(r.data.items || []);
      setStats(r.data.stats || {});
      setTypes(r.data.types || []);
      setErnsten(r.data.ernsten || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); loadInbox(); /* eslint-disable-next-line */ }, [filterErnst, filterType]);

  const toggleOuderInfo = async (item) => {
    try {
      await api.put(`/incidenten/${item.id}`, { ouder_geinformeerd: !item.ouder_geinformeerd });
      toast.success("Bijgewerkt");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
  };

  const closeIncident = async (item) => {
    try {
      await api.put(`/incidenten/${item.id}`, { afgesloten: true });
      toast.success("Incident afgesloten");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
  };

  return (
    <div className="min-h-screen bg-neutral-50 p-4 md:p-6" data-testid="incidenten-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-display text-neutral-900 flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-amber-600" /> Incidenten
            </h1>
            <p className="text-sm text-neutral-600 mt-1">Registreer en volg incidenten voor GGD-inspectie. Alle wijzigingen worden geaudit.</p>
          </div>
          <div className="flex items-center gap-2">
            {["superadmin", "platform_admin", "directeur"].includes((me?.role || "").toLowerCase()) && (
              <Button
                onClick={() => setShowConfig(true)}
                variant="outline"
                data-testid="workflow-config-btn"
                title="Beoordelaar instellen"
              >
                <Settings className="h-4 w-4 mr-1.5" /> Workflow-config
              </Button>
            )}
            <Button onClick={() => setShowForm(true)} className="text-white" style={{ background: "var(--sage)" }} data-testid="incidenten-nieuw">
              <Plus className="h-4 w-4 mr-1.5" /> Nieuw incident
            </Button>
          </div>
        </div>

        {/* Tab-schakelaar: Alle vs Inbox */}
        <div className="flex gap-2 mb-4 border-b border-neutral-200" data-testid="incidenten-tabs">
          <button
            onClick={() => setTab("alle")}
            data-testid="tab-alle"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              tab === "alle" ? "border-emerald-600 text-emerald-700" : "border-transparent text-neutral-500 hover:text-neutral-700"
            }`}
          >
            Alle incidenten ({items.length})
          </button>
          <button
            onClick={() => setTab("inbox")}
            data-testid="tab-inbox"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${
              tab === "inbox" ? "border-amber-600 text-amber-700" : "border-transparent text-neutral-500 hover:text-neutral-700"
            }`}
          >
            <Inbox className="h-3.5 w-3.5" /> Ter beoordeling
            {inbox.length > 0 && (
              <span className="ml-1 min-w-[18px] px-1.5 h-[18px] inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold">
                {inbox.length}
              </span>
            )}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="incidenten-stats">
          {[
            { label: "Totaal", key: "totaal", cls: "text-neutral-900 bg-white border-neutral-200" },
            { label: "Kritiek", key: "kritiek", cls: "text-red-800 bg-red-50 border-red-200" },
            { label: "Hoog", key: "hoog", cls: "text-orange-800 bg-orange-50 border-orange-200" },
            { label: "Open", key: "open", cls: "text-amber-800 bg-amber-50 border-amber-200" },
            { label: "Niet ouder geïnformeerd", key: "niet_ouder_geinformeerd", cls: "text-blue-800 bg-blue-50 border-blue-200" },
          ].map(s => (
            <div key={s.key} className={`rounded border p-3 ${s.cls}`} data-testid={`incidenten-stat-${s.key}`}>
              <div className="text-[10px] uppercase tracking-widest opacity-70">{s.label}</div>
              <div className="text-2xl font-semibold mt-0.5">{stats[s.key] ?? 0}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <Card className="p-3 mb-3 bg-white flex flex-wrap items-center gap-3" data-testid="incidenten-filters">
          <Filter className="h-4 w-4 text-neutral-500" />
          <Select value={filterErnst} onValueChange={setFilterErnst}>
            <SelectTrigger className="h-9 max-w-[160px]" data-testid="filter-ernst"><SelectValue placeholder="Alle ernst" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Alle ernst</SelectItem>
              {ernsten.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="h-9 max-w-[200px]" data-testid="filter-type"><SelectValue placeholder="Alle types" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Alle types</SelectItem>
              {types.map(t => <SelectItem key={t} value={t}>{TYPE_LABEL[t] || t}</SelectItem>)}
            </SelectContent>
          </Select>
        </Card>

        {/* Inbox-tab: alleen incidenten ter beoordeling naar mij */}
        {tab === "inbox" && (
          <div className="space-y-2" data-testid="incidenten-inbox-list">
            {inbox.length === 0 && (
              <Card className="p-6 text-center text-neutral-500 text-sm bg-white">
                <Inbox className="h-8 w-8 mx-auto mb-2 text-neutral-400" />
                Geen incidenten wachten op jouw beoordeling.
              </Card>
            )}
            {inbox.map(it => (
              <Card key={it.id} className="p-3 bg-white border-l-4 border-amber-400" data-testid={`inbox-incident-${it.id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${ERNST_KLEUR[it.ernst] || ""} text-[10px]`}>{it.ernst}</Badge>
                    <Badge variant="outline" className="text-[10px]">{TYPE_LABEL[it.type] || it.type}</Badge>
                    <span className="text-xs font-mono text-neutral-600">{it.datum}{it.tijd ? ` ${it.tijd}` : ""}</span>
                    <Badge className="bg-amber-100 text-amber-800 text-[10px]">
                      <Clock className="h-3 w-3 mr-1 inline" /> Wacht op review
                    </Badge>
                  </div>
                  <div className="text-[11px] text-neutral-500 text-right">
                    Van <strong>{it.submitted_by_name}</strong>
                  </div>
                </div>
                <div className="text-sm text-neutral-800 whitespace-pre-wrap">{it.beschrijving}</div>
                {it.actie_ondernomen && (
                  <div className="mt-2 text-xs text-neutral-700 bg-neutral-50 rounded p-2 border border-neutral-200">
                    <span className="font-semibold">Actie:</span> {it.actie_ondernomen}
                  </div>
                )}
                {it.submit_toelichting && (
                  <div className="mt-2 text-xs italic text-neutral-700 bg-blue-50 border border-blue-200 rounded p-2">
                    Toelichting: "{it.submit_toelichting}"
                  </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => setReviewingId(it.id)}
                          data-testid={`inbox-review-${it.id}`}>
                    Beoordelen
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {tab === "alle" && (
        <>
        {/* Lijst */}
        <div className="space-y-2">
          {loading && <div className="text-center text-sm text-neutral-500 py-6">Laden…</div>}
          {!loading && items.length === 0 && (
            <Card className="p-8 text-center bg-white" data-testid="incidenten-empty">
              <AlertTriangle className="h-8 w-8 mx-auto text-neutral-300 mb-2" />
              <div className="text-sm text-neutral-600">Geen incidenten gevonden. 🌟</div>
            </Card>
          )}
          {items.map(it => (
            <Card key={it.id} className="p-3 bg-white" data-testid={`incident-${it.id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${ERNST_KLEUR[it.ernst] || ""} text-[10px]`}>{it.ernst}</Badge>
                  <Badge variant="outline" className="text-[10px]">{TYPE_LABEL[it.type] || it.type}</Badge>
                  <span className="text-xs font-mono text-neutral-600">{it.datum}{it.tijd ? ` ${it.tijd}` : ""}</span>
                  {it.afgesloten && <Badge className="bg-emerald-100 text-emerald-800 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1 inline" />Afgesloten</Badge>}
                  {!it.afgesloten && <Badge className="bg-amber-100 text-amber-800 text-[10px]"><Clock className="h-3 w-3 mr-1 inline" />Open</Badge>}
                  <WorkflowBadge status={it.workflow_status} />
                </div>
                <div className="text-[11px] text-neutral-500 text-right">
                  {it.melder_naam}
                </div>
              </div>
              {it.kind_naam && <div className="text-xs text-neutral-600 mb-1">Kind: <strong>{it.kind_naam}</strong></div>}
              <div className="text-sm text-neutral-800 whitespace-pre-wrap">{it.beschrijving}</div>
              {it.actie_ondernomen && (
                <div className="mt-2 text-xs text-neutral-700 bg-neutral-50 rounded p-2 border border-neutral-200">
                  <span className="font-semibold">Actie:</span> {it.actie_ondernomen}
                </div>
              )}
              {it.submitted_to_name && (
                <div className="mt-2 text-[11px] text-neutral-700 bg-blue-50 border border-blue-200 rounded p-2">
                  <Send className="h-3 w-3 inline mr-1" />
                  Verstuurd naar <strong>{it.submitted_to_name}</strong>
                  {it.submitted_by_name && <> door {it.submitted_by_name}</>}
                  {it.review_opmerking && (
                    <div className="mt-1 italic">Beoordeling: "{it.review_opmerking}"</div>
                  )}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                <label className="flex items-center gap-1.5 cursor-pointer" data-testid={`incident-ouder-info-${it.id}`}>
                  <Checkbox checked={it.ouder_geinformeerd} onCheckedChange={() => toggleOuderInfo(it)} />
                  <span className={it.ouder_geinformeerd ? "text-emerald-700" : "text-red-700 font-medium"}>Ouder geïnformeerd</span>
                </label>
                <span className={`text-xs ${it.ggd_gemeld ? "text-emerald-700" : "text-neutral-500"}`}>
                  {it.ggd_gemeld ? "✓ GGD gemeld" : "GGD niet gemeld"}
                </span>
                {(!it.workflow_status || it.workflow_status === "draft" || it.workflow_status === "needs_info") &&
                  it.gemeld_door_user_id === me?.id && (
                  <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                          onClick={() => setSubmittingId(it.id)}
                          data-testid={`incident-submit-${it.id}`}>
                    <Send className="h-3 w-3 mr-1" /> Verzenden ter beoordeling
                  </Button>
                )}
                {!it.afgesloten && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => closeIncident(it)} data-testid={`incident-close-${it.id}`}>
                    Afsluiten
                  </Button>
                )}
                <a
                  href={`${API}/incidenten/${it.id}/pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-auto inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 hover:underline"
                  data-testid={`incident-pdf-${it.id}`}
                >
                  <FileDown className="h-3 w-3" /> PDF
                </a>
              </div>
            </Card>
          ))}
        </div>
        </>
        )}

        {reviewingId && (
          <ReviewModal
            incidentId={reviewingId}
            onClose={() => setReviewingId(null)}
            onDone={() => { setReviewingId(null); load(); loadInbox(); }}
          />
        )}
        {submittingId && (
          <SubmitModal
            incidentId={submittingId}
            onClose={() => setSubmittingId(null)}
            onDone={() => { setSubmittingId(null); load(); loadInbox(); }}
          />
        )}

        {showForm && <IncidentForm types={types} ernsten={ernsten} onClose={() => setShowForm(false)} onSaved={load} />}
        {showConfig && <WorkflowConfigModal onClose={() => setShowConfig(false)} />}
      </div>
    </div>
  );
}

function IncidentForm({ types, ernsten, onClose, onSaved }) {
  const { user } = useAuth();
  const isGroup = user?.role === "groep_account";
  const [form, setForm] = useState({
    datum: new Date().toISOString().slice(0, 10),
    tijd: new Date().toTimeString().slice(0, 5),
    type: types[0] || "overig",
    ernst: "laag",
    beschrijving: "",
    actie_ondernomen: "",
    ouder_geinformeerd: false,
    ggd_gemeld: false,
  });
  const [saving, setSaving] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  const doSave = async (extra = {}) => {
    if (form.beschrijving.trim().length < 3) { toast.error("Beschrijving is verplicht (min 3 tekens)"); return; }
    setSaving(true);
    try {
      // Zorg dat group_id + location_id zijn gezet voor groep_account
      const payload = { ...form, ...extra };
      if (isGroup) {
        payload.group_id = user.bound_group_id;
        payload.location_id = user.bound_location_id;
      }
      await api.post("/incidenten", payload);
      toast.success("Incident geregistreerd");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const save = async () => {
    if (isGroup) { setPinOpen(true); return; }
    await doSave();
  };

  const onPinConfirmed = async (data) => {
    await doSave({
      performer_medewerker_user_id: data.medewerker_user_id,
      performer_pin: data.pin,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="incident-form">
        <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold text-neutral-900">Nieuw incident registreren</div>
            <div className="text-xs text-neutral-500">Voor GGD-inspectieproof logging</div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Datum *</Label>
              <Input type="date" value={form.datum} onChange={e => setForm({ ...form, datum: e.target.value })} data-testid="incident-datum" />
            </div>
            <div>
              <Label>Tijd</Label>
              <Input type="time" value={form.tijd} onChange={e => setForm({ ...form, tijd: e.target.value })} data-testid="incident-tijd" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type *</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="incident-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {types.map(t => <SelectItem key={t} value={t}>{TYPE_LABEL[t] || t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ernst *</Label>
              <Select value={form.ernst} onValueChange={v => setForm({ ...form, ernst: v })}>
                <SelectTrigger data-testid="incident-ernst"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {ernsten.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Beschrijving *</Label>
            <Textarea rows={4} value={form.beschrijving} onChange={e => setForm({ ...form, beschrijving: e.target.value })} placeholder="Wat is er gebeurd? Wees zo feitelijk mogelijk." data-testid="incident-beschrijving" />
          </div>
          <div>
            <Label>Actie ondernomen</Label>
            <Textarea rows={3} value={form.actie_ondernomen} onChange={e => setForm({ ...form, actie_ondernomen: e.target.value })} placeholder="Welke acties zijn direct genomen?" data-testid="incident-actie" />
          </div>
          <div className="space-y-2 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.ouder_geinformeerd} onCheckedChange={v => setForm({ ...form, ouder_geinformeerd: !!v })} data-testid="incident-ouder-check" />
              <span className="text-sm">Ouder(s) geïnformeerd</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.ggd_gemeld} onCheckedChange={v => setForm({ ...form, ggd_gemeld: !!v })} data-testid="incident-ggd-check" />
              <span className="text-sm">GGD gemeld (verplicht bij kritieke incidenten)</span>
            </label>
          </div>
        </div>
        <div className="p-5 border-t border-neutral-200 flex justify-between">
          <Button variant="outline" onClick={onClose} data-testid="incident-cancel">Annuleren</Button>
          <Button onClick={save} disabled={saving} className="text-white" style={{ background: "var(--sage)" }} data-testid="incident-save">
            {saving ? "Opslaan…" : (isGroup ? "Registreren met PIN" : "Registreren")}
          </Button>
        </div>
      </div>
      <StaffPinModal
        open={pinOpen}
        onOpenChange={setPinOpen}
        onConfirm={onPinConfirmed}
        title="Wie meldt dit incident?"
        description="Groepslogin: identificeer de melder via medewerker + PIN."
      />
    </div>
  );
}


// ============================================================
// Workflow componenten — badge, submit-modal, review-modal
// ============================================================
const WORKFLOW_STYLE = {
  draft: { cls: "bg-neutral-100 text-neutral-700", label: "Concept" },
  in_review: { cls: "bg-blue-100 text-blue-800", label: "Ter beoordeling" },
  needs_info: { cls: "bg-orange-100 text-orange-800", label: "Aanvulling nodig" },
  approved: { cls: "bg-emerald-100 text-emerald-800", label: "Geaccordeerd" },
  rejected: { cls: "bg-red-100 text-red-800", label: "Afgewezen" },
};

function WorkflowBadge({ status }) {
  if (!status) return null;
  const s = WORKFLOW_STYLE[status];
  if (!s) return null;
  return <Badge className={`${s.cls} text-[10px]`} data-testid={`workflow-badge-${status}`}>{s.label}</Badge>;
}

function SubmitModal({ incidentId, onClose, onDone }) {
  const [reviewers, setReviewers] = useState([]);
  const [selected, setSelected] = useState("");
  const [toelichting, setToelichting] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Haal workflow-config default reviewer op
        const cfg = await api.get("/incidenten-workflow-config");
        if (cfg.data?.reviewer_user_id) setSelected(cfg.data.reviewer_user_id);
        // Lijst mogelijke reviewers = alle directeurs + superadmins in tenant
        const r = await api.get("/users");
        const users = (r.data?.items || r.data || []).filter(u =>
          ["directeur", "superadmin"].includes((u.role || "").toLowerCase()) && u.active !== false
        );
        setReviewers(users);
      } catch { setReviewers([]); }
    })();
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/incidenten/${incidentId}/submit`, {
        reviewer_user_id: selected || undefined,
        toelichting: toelichting || undefined,
      });
      toast.success("Incident verzonden ter beoordeling");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="submit-modal">
        <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
          <div className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <Send className="h-5 w-5 text-blue-600" /> Verzenden ter beoordeling
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label className="text-sm">Beoordelaar</Label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="submit-reviewer-select"><SelectValue placeholder="Selecteer directeur" /></SelectTrigger>
              <SelectContent className="bg-white">
                {reviewers.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name || u.email} ({u.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral-500 mt-1">De beoordelaar krijgt een pushmelding.</p>
          </div>
          <div>
            <Label className="text-sm">Toelichting (optioneel)</Label>
            <Textarea rows={3} value={toelichting} onChange={e => setToelichting(e.target.value)}
                      placeholder="Bijv. 'Graag vandaag beoordelen'" data-testid="submit-toelichting" />
          </div>
        </div>
        <div className="p-5 border-t border-neutral-200 flex justify-between">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={submit} disabled={busy || !selected} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-confirm">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            {busy ? "Verzenden…" : "Verzenden"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReviewModal({ incidentId, onClose, onDone }) {
  const [incident, setIncident] = useState(null);
  const [opmerking, setOpmerking] = useState("");
  const [busy, setBusy] = useState(null);  // welke beslissing loopt

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/incidenten/${incidentId}`);
        setIncident(r.data);
      } catch { setIncident({ error: true }); }
    })();
  }, [incidentId]);

  const doReview = async (beslissing) => {
    if (beslissing !== "approved" && opmerking.trim().length < 3) {
      toast.error("Geef een opmerking op om terug te sturen of af te wijzen");
      return;
    }
    setBusy(beslissing);
    try {
      await api.post(`/incidenten/${incidentId}/review`, {
        beslissing, opmerking: opmerking || undefined,
      });
      toast.success(`Incident: ${beslissing}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="review-modal">
        <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
          <div className="text-lg font-semibold text-neutral-900">Incident beoordelen</div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        {incident && !incident.error && (
          <div className="p-5 space-y-3 text-sm">
            <div className="bg-neutral-50 border border-neutral-200 rounded p-3">
              <div className="flex flex-wrap gap-2 mb-2 items-center">
                <Badge className={`${ERNST_KLEUR[incident.ernst] || ""} text-[10px]`}>{incident.ernst}</Badge>
                <Badge variant="outline" className="text-[10px]">{TYPE_LABEL[incident.type] || incident.type}</Badge>
                <span className="text-xs font-mono text-neutral-600">{incident.datum} {incident.tijd || ""}</span>
              </div>
              <div className="text-xs text-neutral-500 mb-1">Van: <strong>{incident.submitted_by_name}</strong></div>
              <div className="whitespace-pre-wrap text-neutral-800">{incident.beschrijving}</div>
              {incident.actie_ondernomen && (
                <div className="mt-2 text-xs text-neutral-700 border-t border-neutral-200 pt-2">
                  <span className="font-semibold">Actie:</span> {incident.actie_ondernomen}
                </div>
              )}
              {incident.submit_toelichting && (
                <div className="mt-2 italic text-xs text-neutral-600">Toelichting: "{incident.submit_toelichting}"</div>
              )}
            </div>
            <div>
              <Label className="text-sm">Jouw opmerking</Label>
              <Textarea rows={3} value={opmerking} onChange={e => setOpmerking(e.target.value)}
                        placeholder="Bij afwijzen/terugsturen: verplicht"
                        data-testid="review-opmerking" />
            </div>
          </div>
        )}
        <div className="p-5 border-t border-neutral-200 flex justify-between gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose} disabled={!!busy}>Annuleren</Button>
          <div className="flex gap-2">
            <Button variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    disabled={!!busy}
                    onClick={() => doReview("needs_info")}
                    data-testid="review-needs-info">
              {busy === "needs_info" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
              Terugsturen
            </Button>
            <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"
                    disabled={!!busy}
                    onClick={() => doReview("rejected")}
                    data-testid="review-reject">
              {busy === "rejected" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ThumbsDown className="h-4 w-4 mr-1" />}
              Afwijzen
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={!!busy}
                    onClick={() => doReview("approved")}
                    data-testid="review-approve">
              {busy === "approved" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ThumbsUp className="h-4 w-4 mr-1" />}
              Accorderen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


function WorkflowConfigModal({ onClose }) {
  const [config, setConfig] = useState(null);
  const [reviewers, setReviewers] = useState([]);
  const [reviewerId, setReviewerId] = useState("");
  const [senderRole, setSenderRole] = useState("pedagogisch");
  const [emailNotify, setEmailNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cfg, users, suggest] = await Promise.all([
          api.get("/incidenten-workflow-config"),
          api.get("/users"),
          api.get("/incidenten-workflow-suggest-reviewer").catch(() => ({ data: {} })),
        ]);
        setConfig(cfg.data);
        // Als er al een reviewer is → gebruik die. Anders auto-suggest.
        const existingReviewer = cfg.data?.reviewer_user_id;
        if (existingReviewer) {
          setReviewerId(existingReviewer);
        } else if (suggest.data?.reviewer_user_id) {
          setReviewerId(suggest.data.reviewer_user_id);
          toast.info(`Voorgesteld: ${suggest.data.reviewer_name}`, {
            description: suggest.data.reason === "recently_active"
              ? "Deze directeur was recent actief in het systeem."
              : "Bevestig deze keuze om de workflow te activeren.",
          });
        }
        setSenderRole(cfg.data?.sender_role || "pedagogisch");
        setEmailNotify(cfg.data?.email_notify ?? true);
        const usrs = (users.data?.items || users.data || [])
          .filter(u => ["directeur", "superadmin"].includes((u.role || "").toLowerCase()) && u.active !== false);
        setReviewers(usrs);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail || e.message));
      }
    })();
  }, []);

  const save = async () => {
    if (!reviewerId) { toast.error("Kies een beoordelaar"); return; }
    setBusy(true);
    try {
      await api.put("/incidenten-workflow-config", {
        sender_role: senderRole || null,
        reviewer_user_id: reviewerId,
        email_notify: emailNotify,
      });
      toast.success("Workflow-config opgeslagen");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="workflow-config-modal">
        <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
          <div className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <Settings className="h-5 w-5 text-neutral-700" /> Incidenten-workflow
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-neutral-600 mb-2">
            Bepaal wie standaard incident-registraties beoordeelt. Medewerkers kunnen dit
            per keer overschrijven bij het verzenden.
          </p>
          <div>
            <Label className="text-sm">Beoordelaar *</Label>
            <Select value={reviewerId} onValueChange={setReviewerId}>
              <SelectTrigger data-testid="config-reviewer-select"><SelectValue placeholder="Selecteer directeur" /></SelectTrigger>
              <SelectContent className="bg-white">
                {reviewers.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name || u.email} ({u.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Rol die mag verzenden</Label>
            <Select value={senderRole} onValueChange={setSenderRole}>
              <SelectTrigger data-testid="config-sender-role-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="pedagogisch">Pedagogisch medewerker</SelectItem>
                <SelectItem value="locatiemanager">Locatiemanager</SelectItem>
                <SelectItem value="administratie">Administratie</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral-500 mt-1">Alle rollen kunnen incidenten registreren; deze instelling is informatief.</p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={emailNotify} onCheckedChange={setEmailNotify} data-testid="config-email-notify" />
            <span>Beoordelaar ook per e-mail informeren (naast push)</span>
          </label>
        </div>
        <div className="p-5 border-t border-neutral-200 flex justify-between">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={save} disabled={busy || !reviewerId} className="text-white" style={{ background: "var(--sage)" }} data-testid="config-save-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {busy ? "Opslaan…" : "Opslaan"}
          </Button>
        </div>
      </div>
    </div>
  );
}

